"use client"



import { useEffect, useId, useRef } from "react"
import { X } from "lucide-react"
import { useI18n } from "@/i18n/client"
import { useRemote } from "@/lib/api-client"
import { Button } from "@/components/ui/button"
import { FormInput as Input } from "@/components/forms/form-input"
import { Field, FieldLabel, FieldDescription } from "@/components/ui/field"
import { InputGroup, InputGroupInput, InputGroupAddon, InputGroupButton } from "@/components/ui/input-group"
import { Tooltip, TooltipTrigger, TooltipContent, TooltipProvider } from "@/components/ui/tooltip"
import { useWizardFieldError } from "./wizard-validation-context"

export type BookingContact = { name: string; email: string; phone: string; organization: string }
type SavedContact = BookingContact & { userId: string }

export function BookingContactFields({ value, onChange, guestBooking, onGuestChange, fixed = false, disabled = false, includeOrganization = false, accountOnly = false }: {
  value: BookingContact
  onChange: (patch: Partial<BookingContact>) => void
  guestBooking: boolean
  onGuestChange: (guest: boolean) => void
  fixed?: boolean
  disabled?: boolean
  includeOrganization?: boolean
  accountOnly?: boolean
}) {
  const { locale } = useI18n(); const es = locale === "es"
  const id = useId(); const emailInput = useRef<HTMLInputElement>(null)
  const nameError = useWizardFieldError("name"), emailError = useWizardFieldError("email"), phoneError = useWizardFieldError("phone")
  const applied = useRef<SavedContact | null>(null)
  const remote = useRemote<{ contact: SavedContact | null }>(fixed ? null : "/api/booking/contact/", { revalidateOnFocus: true })
  const saved = remote.data?.contact
  const usingSaved = Boolean(saved && !guestBooking && saved.email.toLowerCase() === value.email.trim().toLowerCase())
  const otherEmail = es ? "Introduce otra dirección de correo" : "Enter another email address"

  useEffect(() => {
    if (fixed || (!accountOnly && guestBooking) || !saved || (applied.current?.userId === saved.userId && (!accountOnly || !guestBooking && value.email.trim().toLowerCase() === saved.email.toLowerCase()))) return
    if (!accountOnly && !applied.current && value.email.trim() && value.email.trim().toLowerCase() !== saved.email.toLowerCase()) {
      // Preserve a visitor's edited or restored draft when the account lookup arrives later.
      onGuestChange(true)
      return
    }
    const switchedAccount = applied.current && applied.current.userId !== saved.userId
    applied.current = saved
    if (accountOnly && guestBooking) onGuestChange(false)
    onChange({
      name: switchedAccount ? saved.name : value.name || saved.name,
      email: saved.email,
      phone: switchedAccount ? saved.phone : value.phone || saved.phone,
      organization: switchedAccount ? saved.organization : value.organization || saved.organization,
    })
  }, [fixed, accountOnly, guestBooking, saved, value.name, value.email, value.phone, value.organization, onChange, onGuestChange])

  function detach() {
    onGuestChange(true)
    onChange({ email: "" })
    requestAnimationFrame(() => emailInput.current?.focus())
  }
  function restore() {
    if (!saved) return
    applied.current = saved
    onChange({ name: saved.name, email: saved.email, phone: saved.phone, organization: saved.organization })
    onGuestChange(false)
  }

  return <>
    <Field>
      <FieldLabel htmlFor={id + "-name"}>{es ? "Nombre completo" : "Full name"} *</FieldLabel>
      <Input id={id + "-name"} name="name" value={value.name} onChange={event => onChange({ name: event.target.value })} readOnly={fixed} disabled={disabled} autoComplete="name" required minLength={2} maxLength={140} aria-invalid={!!nameError} aria-describedby={nameError ? id + "-name-error" : undefined} />
      {nameError ? <FieldDescription id={id + "-name-error"} className="text-destructive">{nameError}</FieldDescription> : null}
    </Field>
    <Field>
      <FieldLabel htmlFor={id + "-email"}>{es ? "Correo electrónico" : "Email address"} *</FieldLabel>
      <InputGroup className="min-h-12">
        <InputGroupInput ref={emailInput} id={id + "-email"} name="email" type="email" value={value.email} onChange={event => onChange({ email: event.target.value })} readOnly={fixed || usingSaved || accountOnly} disabled={disabled} autoComplete="email" required maxLength={254} aria-invalid={!!emailError} aria-describedby={id + "-contact-help" + (emailError ? " " + id + "-email-error" : "")} />
        {usingSaved && !fixed && !accountOnly ? <InputGroupAddon align="inline-end" className="py-0">
          <TooltipProvider><Tooltip><TooltipTrigger render={<InputGroupButton type="button" size="icon-sm" className="size-11" aria-label={otherEmail} title={otherEmail} disabled={disabled} onClick={detach} />}><X /></TooltipTrigger><TooltipContent>{otherEmail}</TooltipContent></Tooltip></TooltipProvider>
        </InputGroupAddon> : null}
      </InputGroup>
      {emailError ? <FieldDescription id={id + "-email-error"} className="text-destructive">{emailError}</FieldDescription> : null}
      <FieldDescription id={id + "-contact-help"} aria-live="polite">
        {accountOnly ? "This request belongs to your signed-in account. Add a different passenger below." : fixed ? (es ? "Cliente seleccionado en el panel." : "Customer selected in management.") : usingSaved ? (es ? "Datos de tu cuenta. Pulsa × para usar otro correo." : "Using your saved account details. Select × to use another email address.") : guestBooking ? (es ? "Esta solicitud usará el correo que introduzcas. El correo de tu cuenta no cambiará." : "This request will use the email you enter. Your account email stays unchanged.") : remote.isLoading ? (es ? "Cargando los datos guardados…" : "Loading saved contact details…") : (es ? "Puedes continuar sin crear una cuenta." : "You can continue without creating an account.")}
      </FieldDescription>
      {saved && !usingSaved && !fixed ? <Button type="button" variant="link" className="h-auto justify-start p-0" disabled={disabled} onClick={restore}>{es ? "Usar los datos de mi cuenta" : "Use my account details"}</Button> : null}
      {remote.error ? <FieldDescription>{accountOnly ? "Your account details could not be loaded." : es ? "No se pudieron cargar los datos guardados. Puedes introducirlos manualmente." : "Saved details could not be loaded. You can enter your details manually."} {accountOnly && <Button type="button" variant="link" onClick={() => void remote.mutate()}>Retry</Button>}</FieldDescription> : null}
    </Field>
    <Field>
      <FieldLabel htmlFor={id + "-phone"}>{es ? "Teléfono" : "Phone number"} *</FieldLabel>
      <Input id={id + "-phone"} name="phone" type="tel" value={value.phone} onChange={event => onChange({ phone: event.target.value })} readOnly={fixed} disabled={disabled} autoComplete="tel" required minLength={7} maxLength={35} aria-invalid={!!phoneError} aria-describedby={phoneError ? id + "-phone-error" : undefined} />
      {phoneError ? <FieldDescription id={id + "-phone-error"} className="text-destructive">{phoneError}</FieldDescription> : null}
    </Field>
    {includeOrganization ? <Field>
      <FieldLabel htmlFor={id + "-organization"}>{es ? "Empresa, familia u organizador" : "Company, family or event organizer"}</FieldLabel>
      <Input id={id + "-organization"} name="organization" value={value.organization} onChange={event => onChange({ organization: event.target.value })} disabled={disabled} readOnly={fixed} autoComplete="organization" maxLength={180} />
    </Field> : null}
  </>
}
